import DateTimeRange from 'admin/components/atoms/DateTimeRange';
import Headings from 'admin/components/atoms/Headings';
import Label from 'admin/components/atoms/Label';
import Labeled from 'admin/components/atoms/Labeled';
import NumberInput from 'admin/components/atoms/NumberInput';
import TextArea from 'admin/components/atoms/TextArea';
import TextBox from 'admin/components/atoms/TextBox';
import { toPointExpiresAtFormValue } from 'admin/utils/pointExpiry';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import styled, { StyledComponentPropsWithRef } from 'styled-components';
import { getCheck, updateCheck } from '~/javascripts/admin/actions/checks';
import { useAppSelector } from '~/javascripts/admin/hooks/useApp';
import { Button } from '../../atoms/Buttons';
import { createColorCodedOptions } from '../../atoms/ColorCodedOption';
import CopyToClipboard from '../../atoms/CopyToClipboard';
import Select from '../../atoms/Select';
import Tooltip from '../../atoms/Tooltip';
import ApiResults from '../../molecules/ApiResults';
import BreadCrumb from '../../molecules/BreadCrumb';
import QrCodeDrawer from '../../molecules/QrCodeDrawer';

const CRUMBS = [
  { value: 'ホーム', href: '/', isCurrentPage: false },
  { value: '販促管理', href: '', isCurrentPage: true },
  { value: 'チャージQRコード管理', href: '/promotion/checks', isCurrentPage: false },
  { value: 'チャージQRコード詳細', href: '', isCurrentPage: true },
];

const FormWrapper = styled.div`
  ${Labeled} {
    display: grid;
    grid-template-columns: 1fr 4fr;
    grid-template-rows: auto;
    justify-items: left;
    padding: 16px 0;
    align-items: center;
    border-bottom: solid;
    border-bottom-width: 1px;
    border-bottom-color: #e8e8e8;
    align-items: flex-start;
  }
  ${Labeled} > ${Label} {
    grid-column: 1;
  }
  ${TextBox} {
    width: 500px;
    margin-bottom: 10px;
    &.amountArrayInput {
      width: 200px;
    }
  }
  ${NumberInput} {
    width: 200px;
    margin-bottom: 10px;
  }
  ${TextArea} {
    width: 500px;
    margin-bottom: 10px;
    resize: both;
  }
`;

const LabelValue = styled.span``;

const StoreDetailWrapper = styled.div``;

const StoreDetail = styled.div``;

const SubmitButtonWrapper = styled.div`
  margin-top: 10px;
`;

const SerialWrapper = styled.div`
  display: flex;
  gap: 12px;
  margin: 10px;
`;

const DistributionFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const DistributionField = styled.div`
  display: flex;
  flex-direction: column;
`;

const FieldLabel = styled.div`
  margin-bottom: 4px;
`;

const STATUS_OPTIONS = [
  {
    label: '開始前',
    value: 'not-started',
    bgColor: '#2684FF',
  },
  {
    label: '有効',
    value: 'valid',
    bgColor: '#1EAC21',
  },
  {
    label: '停止中',
    value: 'paused',
    bgColor: '#735703',
  },
  {
    label: '上限到達',
    value: 'usage-limit-reached',
    bgColor: '#525252',
  },
  {
    label: '期限切れ',
    value: 'expired',
    bgColor: '#525252',
  },
];

const STATUS_TRANSITIONS: Record<string, string[]> = {
  valid: ['paused'],
  paused: ['valid'],
  'not-started': ['valid', 'paused'],
  'usage-limit-reached': [],
  expired: [],
};

const formatOptionLabel = createColorCodedOptions(STATUS_OPTIONS);

const ChecksDetails = ({ match }: RouteComponentProps<{ id?: string }>) => {
  const [check, setCheck] = useState<Awaited<ReturnType<typeof getCheck>>>();
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>();
  const [apiMessages, setApiMessages] =
    useState<StyledComponentPropsWithRef<typeof ApiResults>['messages']>(undefined);

  const user = useAppSelector((state) => state.user);
  const formatDate = (d) => (d ? moment(d).format('YYYY/MM/DD') : d);

  const saveStatus = (id: string) => {
    const params: Record<string, unknown> = {
      is_disabled: (selectedStatus === 'paused').toString(),
    };
    if (check?.status === 'not-started') {
      params.starts_at = moment().toISOString();
    }
    updateCheck(id, params)
      .then(() => getCheck(id))
      .then((res) => {
        setCheck(res);
        setSelectedStatus(res.status);
        setApiMessages([
          {
            text: 'チャージQRコードの更新に成功しました',
            isError: false,
          },
        ]);
      })
      .catch((maybeErrorPromise) => {
        Promise.resolve(maybeErrorPromise).then((error) => {
          setApiMessages([
            {
              text: error.message,
              isError: true,
            },
          ]);
        });
      });
  };

  useEffect(() => {
    if (match.params.id) {
      getCheck(match.params.id).then((res) => {
        setCheck(res);
        setSelectedStatus(res.status);
      });
    }
  }, [match.params.id]);

  return (
    <FormWrapper>
      <BreadCrumb crumbs={CRUMBS} />
      <Headings.h5>チャージQRコード詳細</Headings.h5>
      <ApiResults messages={apiMessages} />
      {!!check && (
        <>
          <Labeled labelText="チャージQRコードID">
            <LabelValue>{check?.id}</LabelValue>
          </Labeled>
          <Labeled labelText="発行日時">
            <LabelValue>{moment(check?.created_at).format('YYYY/MM/DD HH:mm:ss')}</LabelValue>
          </Labeled>
          <Labeled labelText="チャージ元店舗">
            <StoreDetailWrapper>
              <StoreDetail>{`チャージ元組織： ${check?.org_name ?? ''}`}</StoreDetail>
              <StoreDetail style={{ display: 'flex' }}>
                <div style={{ wordBreak: 'keep-all' }}>{`チャージ元店舗： `}</div>
                <div style={{ wordBreak: 'break-word' }}>{check?.user?.name ?? ''}</div>
              </StoreDetail>
            </StoreDetailWrapper>
          </Labeled>
          <Labeled labelText="用途">
            <LabelValue>{check?.note}</LabelValue>
          </Labeled>
          <Labeled labelText="マネー">
            <LabelValue>{check?.money_amount?.toLocaleString()}</LabelValue>
          </Labeled>
          <Labeled labelText="ポイント">
            <LabelValue>{check?.point_amount?.toLocaleString()}</LabelValue>
          </Labeled>
          <Labeled labelText="ポイント有効期限(日付指定)">
            <LabelValue>
              {formatDate(toPointExpiresAtFormValue(check?.point_expires_at))}
            </LabelValue>
          </Labeled>
          <Labeled labelText="ポイント有効期限(相対日付指定)">
            <LabelValue>{check?.point_expires_in_days}</LabelValue>
          </Labeled>
          <Labeled labelText="ポイント負担先店舗">
            <LabelValue style={{ wordBreak: 'break-word' }}>
              {check?.bear_point_shop?.name}
            </LabelValue>
          </Labeled>
          <Labeled labelText="説明文">
            <LabelValue style={{ wordBreak: 'break-word' }}>{check?.description}</LabelValue>
          </Labeled>
          <Labeled labelText="最終利用日時">
            <LabelValue>
              {!!check.last_used_at && moment(check?.last_used_at).format('YYYY/MM/DD HH:mm:ss')}
            </LabelValue>
          </Labeled>
          <Labeled labelText="配布設定">
            <DistributionFields>
              <DistributionField>
                <FieldLabel>読取可能期間</FieldLabel>
                <DateTimeRange
                  dateRange={{
                    from: check?.starts_at ? moment(check.starts_at) : null,
                    to: check?.expires_at ? moment(check.expires_at) : null,
                  }}
                  dateTimeProps={{
                    fromProps: { inputProps: { disabled: true } },
                    toProps: { inputProps: { disabled: true } },
                  }}
                />
              </DistributionField>
              <DistributionField>
                <FieldLabel>利用人数</FieldLabel>
                <TextBox
                  disabled
                  readOnly
                  style={{ width: '200px' }}
                  syncValue={(() => {
                    // For one-time checks: show 0/1 or 1/1 based on last_used_at status
                    if (check.is_onetime) {
                      return `${check.last_used_at ? 1 : 0}/1`;
                    }

                    // For regular checks: show count/limit if limit exists
                    return check.usage_limit ? `${check.usage_count}/${check.usage_limit}` : '';
                  })()}
                />
              </DistributionField>
            </DistributionFields>
          </Labeled>

          <Labeled
            labelText={
              <div>
                状態
                <Tooltip
                  id="status-tooltip"
                  text="有効に更新時、読取可能期間内となりすぐにQRコードが読取可能になります"
                />
              </div>
            }
          >
            <Select
              options={STATUS_OPTIONS.filter(
                (o) =>
                  o.value !== selectedStatus &&
                  (o.value === check.status ||
                    (STATUS_TRANSITIONS[check.status] ?? []).includes(o.value))
              )}
              value={STATUS_OPTIONS.find((x) => x.value === selectedStatus)}
              isMulti={false}
              isDisabled={(STATUS_TRANSITIONS[check.status] ?? []).length === 0}
              formatOptionLabel={formatOptionLabel}
              onChange={(option) => setSelectedStatus(option?.value)}
            />
          </Labeled>
          <Labeled labelText="配布物">
            <div>
              <QrCodeDrawer
                uri={check?.token}
                name={check?.user.name}
                qrSize={128}
                showUrl
                urlAsLabel
                copyable
              />
              {check?.serial_code && (
                <SerialWrapper>
                  <Label>{check.serial_code}</Label>
                  <CopyToClipboard text={check.serial_code} tooltipId="serialCode" />
                </SerialWrapper>
              )}
            </div>
          </Labeled>
          <SubmitButtonWrapper>
            <Button
              onClick={() => saveStatus(check.id)}
              disabled={
                user.role === 'ORGANIZATION-STAFF' ||
                (STATUS_TRANSITIONS[check.status] ?? []).length === 0 ||
                selectedStatus === check.status
              }
            >
              更新
            </Button>
          </SubmitButtonWrapper>
        </>
      )}
    </FormWrapper>
  );
};

export default ChecksDetails;
